import { Component, inject, OnInit, ChangeDetectorRef } from '@angular/core';
import { ButtonModule } from 'primeng/button';
import { TabsModule } from 'primeng/tabs';
import { MultiSelectModule } from 'primeng/multiselect';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TableModule } from 'primeng/table';
import { AccountingReportService } from '../../../shared/services/accounting-report-service.service';
import * as XLSX from 'xlsx';
import { PageHeaderComponent } from '@shared/components/page-header/page-header.component';
import { CardComponent } from '@shared/components/card/card.component';

import {
    IAccountingMonthlyReport,
    BookingDateOrFlightDate,
    IAccountingMonthlyReportUnit,
    Metrics,
    YearlyComparisonData,
    MonthlyComparisonData
} from '../../../shared/interfaces/accounting-report.interface';
type UiField = 'cost' | 'revenue' | 'marge' | 'margin' | 'marginAmount';
@Component({
    selector: 'app-yearly-revenue-report',
    imports: [TabsModule, ButtonModule, MultiSelectModule, CommonModule, FormsModule, TableModule, PageHeaderComponent, CardComponent],
    templateUrl: './yearly-revenue-report.component.html',
    styleUrl: './yearly-revenue-report.component.scss'
})
export class YearlyRevenueReportComponent implements OnInit {
    BookingDateOrFlightDate = BookingDateOrFlightDate;
    viewOrCompare: number = 0;
    errorMsg: string = '';
    selectedYears: number[] = [];
    years: number[] = [];
    accountingService = inject(AccountingReportService);
    dateBasis: BookingDateOrFlightDate = BookingDateOrFlightDate.BookingDate;
    revenueData: IAccountingMonthlyReport[] = [];
    comparisonData: YearlyComparisonData | null = null;
    tableData: any[] = [];
    loading: boolean = false;
    months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

    private cd = inject(ChangeDetectorRef);

    ngOnInit(): void {
        const currentYear = new Date().getFullYear();
        for (let i = 0; i < 4; i++) {
            this.years.push(currentYear - i);
        }
        // Varsayılan olarak son 2 yılı seçili yap
        this.selectedYears = [currentYear, currentYear - 1];
        this.searchViewBySelectedYears();
    }

    toggleButton(value: number) {
        this.viewOrCompare = value;
        this.resetSelection();
    }
    resetSelection() {
        this.selectedYears = [];
        this.revenueData = [];
        this.comparisonData = null;
    }
    get compareMsg() {
        const n = this.selectedYears.length;
        if (n === 0) return 'Pick 2 years to compare.';
        if (n === 1) return 'Select 1 more year.';
        if (n === 2) return '';
        return 'You can compare at most 2 years.';
    }

    setDateBasis(basis: BookingDateOrFlightDate) {
        this.dateBasis = basis;
        if (this.viewOrCompare === 0) this.getViewReport(this.dateBasis);
        else if (this.viewOrCompare === 1) this.getCompareReport(this.dateBasis);
    }

    getViewReport(dateBasis: BookingDateOrFlightDate) {
        if (this.viewOrCompare === 0) {
            this.revenueData = [];
            if (this.selectedYears.length === 0) return;
            this.loading = true;
            this.selectedYears.forEach((year) => {
                this.accountingService.getAccountingMonthlyReport(year, dateBasis).subscribe({
                    next: (value: IAccountingMonthlyReport) => {
                        this.revenueData.push(value);
                        this.revenueData.sort((a, b) => b.year - a.year);
                        this.loading = false;
                        this.cd.detectChanges();
                    },
                    error: (err) => {
                        console.error('Error fetching yearly revenue report:', err);
                        this.loading = false;
                        this.cd.detectChanges();
                    }
                });
            });
        }
    }

    getCompareReport(dateBasis: BookingDateOrFlightDate) {
        if (this.viewOrCompare === 1) {
            this.comparisonData = null;
            this.errorMsg = this.compareMsg;
            this.selectedYears.sort((a, b) => a - b);
            if (this.errorMsg.length > 0) {
                return;
            }
            this.loading = true;
            this.accountingService.getAccountingMonthlyReportComparison(this.selectedYears, dateBasis).subscribe({
                next: (value) => {
                    this.comparisonData = value as unknown as YearlyComparisonData;
                    this.revenueData = [this.comparisonData.year1Report, this.comparisonData.year2Report];
                    this.getComparisonYears();
                    this.loading = false;
                    this.cd.detectChanges();
                },
                error: (err) => {
                    console.error('Error fetching yearly revenue report:', err);
                    this.loading = false;
                    this.cd.detectChanges();
                }
            });
        }
    }
    searchViewBySelectedYears() {
        this.dateBasis = BookingDateOrFlightDate.BookingDate;
        if (this.viewOrCompare === 0) {
            this.getViewReport(this.dateBasis);
        } else if (this.viewOrCompare === 1) {
            this.getCompareReport(this.dateBasis);
        }
    }

    getMonthlyValue(year: number, monthIndex: number, field: string) {
        const rpt = this.revenueData.find((r) => r.year === year);
        if (!rpt) return '';
        const m: IAccountingMonthlyReportUnit | undefined = rpt.monthly?.find((x) => x.month === monthIndex);
        if (!m) return '';
        const metrics: Metrics = m.metrics;
        return this.getDataByField(metrics, field);
    }
    getTotalValue(year: number, field: string) {
        const rpt = this.revenueData.find((r) => r.year === year);
        if (!rpt) return '';
        const metrics: Metrics = rpt.total;
        return this.getDataByField(metrics, field);
    }
    getReservationCount(year: number, monthIndex: number) {
        const rpt = this.revenueData.find((r) => r.year === year);
        if (!rpt) return '';
        const m: IAccountingMonthlyReportUnit | undefined = rpt.monthly?.find((x) => x.month === monthIndex + 1);
        if (!m) return '0';
        return m.reservationCount;
    }
    getPassengerCount(year: number, monthIndex: number) {
        const rpt = this.revenueData.find((r) => r.year === year);
        if (!rpt) return '';
        const m: IAccountingMonthlyReportUnit | undefined = rpt.monthly?.find((x) => x.month === monthIndex + 1);
        if (!m) return '0';
        return m.passengerCount;
    }
    getTotalReservationCount(year: number) {
        const rpt = this.revenueData.find((r) => r.year === year);
        if (!rpt) return '0';
        return rpt.totalReservationCount;
    }
    getTotalPassengerCount(year: number) {
        const rpt = this.revenueData.find((r) => r.year === year);
        if (!rpt) return '0';
        return rpt.totalPassengerCount;
    }
    getDataByField(metrics: Metrics, field: string) {
        switch (field as UiField) {
            case 'cost':
                return this.formatMoneyAmount(metrics.cost.amount);
            case 'revenue':
                return this.formatMoneyAmount(metrics.revenue.amount);
            case 'marginAmount':
                return this.formatMoneyAmount(metrics.revenue.amount - metrics.cost.amount);
            case 'marge':
                return String(metrics.profitRate);
            case 'margin':
                return metrics.marginPercentage.toFixed(2) + ' %';
            default:
                return '';
        }
    }

    private formatMoneyAmount(value: number): string {
        const rounded = Math.round(value);
        if (rounded === 0) return '-';
        return rounded.toLocaleString('de-DE', { maximumFractionDigits: 0 });
    }

    getComparisonYears() {
        this.selectedYears = [];
        if (this.comparisonData) {
            this.selectedYears = [this.comparisonData.year1, this.comparisonData.year2];
        }
    }
    getMonthlyComparison(field: string, monthIndex: number): string | number {
        if (this.comparisonData) {
            const m: MonthlyComparisonData | undefined = this.comparisonData.monthlyComparisons.find((c) => c.month === monthIndex + 1);
            switch (field) {
                case 'reservationCount':
                    return m?.reservationCount || 0;
                case 'passengerCount':
                    return m?.passengerCount || 0;
                default:
                    return 0;
            }
        }
        return 0;
    }
    formatDateBasisLabel(basis: BookingDateOrFlightDate): string {
        return basis.substring(0, 1).toUpperCase() + basis.substring(1).toLowerCase();
    }

    getTextColorByNegativeOrPositive(val: any) {
        if (val < 0) {
            return 'font-medium !text-red-500';
        } else if (val > 0) {
            return 'font-medium !text-green-600';
        } else {
            return '!text-black font-medium';
        }
    }
    exportToExcel() {
        if (this.viewOrCompare === 0) {
            this.exportViewReport();
        } else if (this.viewOrCompare === 1) {
            this.exportComparisonReport();
        }
    }

    exportViewReport() {
        const data = this.prepareViewReportData();
        const fileName = `Yearly_Revenue_Report_${this.selectedYears.join('_')}_${this.formatDateBasisLabel(this.dateBasis)}.xlsx`;
        this.downloadExcel(data, fileName);
    }

    exportComparisonReport() {
        const data = this.prepareComparisonReportData();
        const fileName = `Yearly_Revenue_Comparison_${this.selectedYears.join('_')}_${this.formatDateBasisLabel(this.dateBasis)}.xlsx`;
        this.downloadExcel(data, fileName);
    }

    prepareViewReportData() {
        const data: any[] = [];

        const header = ['Month'];
        this.selectedYears.forEach((year) => {
            header.push(
                `${year} - Revenue`,
                `${year} - Cost`,
                `${year} - Margin`,
                `${year} - Margin(%)`
            );
        });
        data.push(header);

        this.months.forEach((month, index) => {
            const row = [month];
            this.selectedYears.forEach((year) => {
                row.push(
                    this.getMonthlyValue(year, index + 1, 'revenue'),
                    this.getMonthlyValue(year, index + 1, 'cost'),
                    this.getMonthlyValue(year, index + 1, 'marginAmount'),
                    this.getMonthlyValue(year, index + 1, 'margin')
                );
            });
            data.push(row);
        });

        const totalRow = ['Total'];
        this.selectedYears.forEach((year) => {
            totalRow.push(
                this.getTotalValue(year, 'revenue'),
                this.getTotalValue(year, 'cost'),
                this.getTotalValue(year, 'marginAmount'),
                this.getTotalValue(year, 'margin')
            );
        });
        data.push(totalRow);
        data.push([]);
        data.push(['Reservation Count Based On Booking Date']);
        data.push(['', ...this.months, 'Total']);

        this.selectedYears.forEach((y) => {
            data.push([
                String(y),
                ...this.months.map((_, i) => this.getMonthlyCount(y, i + 1, 'reservationCount')),
                this.getYearlyCountTotal(y, 'reservationCount')
            ]);
        });

        data.push([]);
        data.push(['Passenger Count Based On Booking Date']);
        data.push(['', ...this.months, 'Total']);

        this.selectedYears.forEach((y) => {
            data.push([
                String(y),
                ...this.months.map((_, i) => this.getMonthlyCount(y, i + 1, 'passengerCount')),
                this.getYearlyCountTotal(y, 'passengerCount')
            ]);
        });

        return data;
    }

    prepareComparisonReportData() {
        const data: any[] = [];
        const header = ['Month'];
        this.selectedYears.forEach((year) => {
            header.push(
                `${year} - Revenue`,
                `${year} - Cost`,
                `${year} - Margin`,
                `${year} - Margin(%)`
            );
        });
        data.push(header);

        this.months.forEach((month, index) => {
            const row = [month];
            this.selectedYears.forEach((year) => {
                row.push(
                    this.getMonthlyValue(year, index + 1, 'revenue'),
                    this.getMonthlyValue(year, index + 1, 'cost'),
                    this.getMonthlyValue(year, index + 1, 'marginAmount'),
                    this.getMonthlyValue(year, index + 1, 'margin')
                );
            });
            data.push(row);
        });
        const totalRow = ['Total'];
        this.selectedYears.forEach((year) => {
            totalRow.push(
                this.getTotalValue(year, 'revenue'),
                this.getTotalValue(year, 'cost'),
                this.getTotalValue(year, 'marginAmount'),
                this.getTotalValue(year, 'margin')
            );
        });
        data.push(totalRow);
        if (this.selectedYears.length !== 2) return data;

        const [yA, yB] = this.selectedYears;
        data.push([]);
        data.push(['Reservation Count (Booking Date)']);
        data.push(['', ...this.months, 'Total']);
        data.push([
            String(yB),
            ...this.months.map((_, i) => this.getMonthlyCount(yB, i + 1, 'reservationCount')),
            this.getYearlyCountTotal(yB, 'reservationCount')
        ]);
        data.push([
            String(yA),
            ...this.months.map((_, i) => this.getMonthlyCount(yA, i + 1, 'reservationCount')),
            this.getYearlyCountTotal(yA, 'reservationCount')
        ]);
        data.push([
            String(yA + 'vs' + yB),
            ...this.months.map((_, i) => this.getMonthlyComparison('reservationCount', i)),
            this.comparisonData?.yearlyComparison?.reservationCount ?? 0
        ]);
        data.push([]);
        data.push(['Passenger Count (Booking Date)']);
        data.push(['', ...this.months, 'Total']);

        data.push([
            String(yB),
            ...this.months.map((_, i) => this.getMonthlyCount(yB, i + 1, 'passengerCount')),
            this.getYearlyCountTotal(yB, 'passengerCount')
        ]);
        data.push([
            String(yA),
            ...this.months.map((_, i) => this.getMonthlyCount(yA, i + 1, 'passengerCount')),
            this.getYearlyCountTotal(yA, 'passengerCount')
        ]);

        data.push([
            String(yA + 'vs' + yB),
            ...this.months.map((_, i) => this.getMonthlyComparison('passengerCount', i)),
            this.comparisonData?.yearlyComparison?.passengerCount ?? 0
        ]);

        return data;
    }
    getMonthlyCount(year: number, monthIndex1to12: number, field: 'reservationCount' | 'passengerCount'): number {
        const rpt = this.revenueData.find((r) => r.year === year);
        if (!rpt) return 0;
        const m = rpt.monthly?.find((x) => x.month === monthIndex1to12);
        return m ? ((m as any)[field] ?? 0) : 0;
    }

    getYearlyCountTotal(year: number, field: 'reservationCount' | 'passengerCount'): number {
        const rpt = this.revenueData.find((r) => r.year === year);
        if (!rpt || !Array.isArray(rpt.monthly)) return 0;
        return rpt.monthly.reduce((acc, m: any) => acc + (m[field] ?? 0), 0);
    }

    downloadExcel(data: any[], fileName: string) {
        const wb = XLSX.utils.book_new();
        const ws = XLSX.utils.aoa_to_sheet(data);
        const colWidths = (data[0] || []).map((_: any, colIndex: number) => {
            const maxLen = Math.max(...(data.map((row) => (row?.[colIndex] != null ? String(row[colIndex]).length : 0)) as number[]), 0);
            return { wch: Math.min(Math.max(maxLen + 2, 12), 50) };
        });
        ws['!cols'] = colWidths;
        const rowHeights = data.map(() => ({ hpt: 25 }));
        ws['!rows'] = rowHeights;
        if (ws['!ref']) {
            const range = XLSX.utils.decode_range(ws['!ref']);
            const headerStyle = {
                font: { bold: true, sz: 12 },
                fill: { fgColor: { rgb: 'F3F4F6' } },
                alignment: { horizontal: 'center', vertical: 'center' },
                border: {
                    top: { style: 'thin', color: { rgb: '000000' } },
                    bottom: { style: 'thin', color: { rgb: '000000' } },
                    left: { style: 'thin', color: { rgb: '000000' } },
                    right: { style: 'thin', color: { rgb: '000000' } }
                }
            };

            const dataStyle = {
                font: { sz: 11 },
                alignment: { horizontal: 'center', vertical: 'center' },
                border: {
                    top: { style: 'thin', color: { rgb: 'CCCCCC' } },
                    bottom: { style: 'thin', color: { rgb: 'CCCCCC' } },
                    left: { style: 'thin', color: { rgb: 'CCCCCC' } },
                    right: { style: 'thin', color: { rgb: 'CCCCCC' } }
                }
            };
            for (let row = range.s.r; row <= range.e.r; row++) {
                for (let col = range.s.c; col <= range.e.c; col++) {
                    const cellAddress = XLSX.utils.encode_cell({ r: row, c: col });
                    if (!ws[cellAddress]) {
                        ws[cellAddress] = { v: '' };
                    }

                    if (row === range.s.r) {
                        ws[cellAddress].s = headerStyle;
                    } else {
                        ws[cellAddress].s = dataStyle;
                    }
                }
            }
        }
        XLSX.utils.book_append_sheet(wb, ws, 'Revenue Report');
        XLSX.writeFile(wb, fileName);
    }
}
